import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { PageHeader } from '@/components/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type ValidationReport,
  appendRows,
  archiveDataset,
  deleteRow,
  getDataset,
  listRows,
  patchRow,
  validateDataset,
} from '@/features/datasets/datasets-api';
import {
  type FieldDefinition,
  type PropertyMapping,
  getMapping,
} from '@/features/mappings/mappings-api';
import { useTheme } from '@/features/theme/ThemeProvider';
import { useFormatters } from '@/i18n/format';
import { ApiError } from '@/lib/api-client';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AllCommunityModule,
  type ColDef,
  type GridReadyEvent,
  ModuleRegistry,
  ValidationModule,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  ShieldCheck,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { type ReactElement, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

ModuleRegistry.registerModules([
  AllCommunityModule,
  ...(import.meta.env.DEV ? [ValidationModule] : []),
]);

const PAGE_SIZE = 100;

export const Route = createFileRoute('/app/datasets/$id')({
  component: DatasetPage,
});

interface GridRow {
  id: string;
  [key: string]: unknown;
}

type PageItem = number | 'ellipsis-start' | 'ellipsis-end';

function buildPageList(current: number, total: number): PageItem[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items: PageItem[] = [1];
  if (current > 3) items.push('ellipsis-start');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) items.push(i);
  if (current < total - 2) items.push('ellipsis-end');
  items.push(total);
  return items;
}

function buildColumnDefs(
  fields: FieldDefinition[],
  errorMap: Map<string, Set<string>>,
): ColDef<GridRow>[] {
  return fields.map((field) => ({
    field: field.name,
    headerName: field.label ?? field.name,
    editable: true,
    cellDataType: cellDataType(field.type),
    cellClassRules: {
      'ring-1 ring-destructive': (params) => {
        const rowId = params.data?.id;
        if (!rowId) return false;
        return errorMap.get(rowId)?.has(field.name) ?? false;
      },
    },
  }));
}

function cellDataType(type: FieldDefinition['type']): string {
  switch (type) {
    case 'number':
    case 'currency':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'date':
      return 'date';
    default:
      return 'text';
  }
}

function DatasetPage(): ReactElement {
  const { t } = useTranslation();
  const { dateTime } = useFormatters();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const gridRef = useRef<AgGridReact<GridRow>>(null);
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [page, setPage] = useState<number>(1);
  const { resolvedTheme } = useTheme();
  const gridThemeClass = resolvedTheme === 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz';

  const dataset = useQuery({
    queryKey: ['datasets', 'detail', id],
    queryFn: () => getDataset(id),
    retry: false,
  });

  const mapping = useQuery({
    queryKey: ['mappings', 'detail', dataset.data?.dataset.mappingId],
    queryFn: () => getMapping(dataset.data?.dataset.mappingId as string),
    enabled: !!dataset.data?.dataset.mappingId,
  });

  const rows = useQuery({
    queryKey: ['datasets', 'rows', id, page, PAGE_SIZE],
    queryFn: () => listRows(id, page, PAGE_SIZE),
    placeholderData: keepPreviousData,
  });

  const append = useMutation({
    mutationFn: (payload: Array<Record<string, unknown>>) => appendRows(id, payload),
    onSuccess: (data) => {
      const lastPage = Math.max(1, Math.ceil(data.dataset.rowCount / PAGE_SIZE));
      setPage(lastPage);
      queryClient.invalidateQueries({ queryKey: ['datasets', 'rows', id] });
      queryClient.invalidateQueries({ queryKey: ['datasets', 'detail', id] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : t('datasets.detail.addFailed')),
  });

  const updateCell = useMutation({
    mutationFn: (payload: { rowId: string; data: Record<string, unknown> }) =>
      patchRow(id, payload.rowId, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasets', 'detail', id] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : t('datasets.detail.updateFailed'));
      queryClient.invalidateQueries({ queryKey: ['datasets', 'rows', id] });
    },
  });

  const removeRows = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const rowId of ids) {
        await deleteRow(id, rowId);
      }
    },
    onSuccess: (_, ids) => {
      toast.success(t('datasets.detail.deleteSuccess', { count: ids.length }));
      const remaining = Math.max(0, (rows.data?.total ?? 0) - ids.length);
      const lastPage = Math.max(1, Math.ceil(remaining / PAGE_SIZE));
      if (page > lastPage) setPage(lastPage);
      queryClient.invalidateQueries({ queryKey: ['datasets', 'rows', id] });
      queryClient.invalidateQueries({ queryKey: ['datasets', 'detail', id] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : t('datasets.detail.deleteFailed')),
  });

  const validate = useMutation({
    mutationFn: () => validateDataset(id),
    onSuccess: (data) => setReport(data.report),
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : t('datasets.detail.validateFailed')),
  });

  const archive = useMutation({
    mutationFn: () => archiveDataset(id),
    onSuccess: () => {
      toast.success(t('datasets.detail.archiveSuccess'));
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
      void navigate({ to: '/app/datasets' });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : t('datasets.detail.archiveFailed')),
  });

  const errorMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const e of report?.errors ?? []) {
      if (!map.has(e.rowId)) map.set(e.rowId, new Set());
      map.get(e.rowId)?.add(e.field);
    }
    return map;
  }, [report]);

  const columnDefs = useMemo<ColDef<GridRow>[]>(() => {
    const fields = mapping.data?.mapping.schema.fields ?? [];
    return [
      {
        field: 'id',
        headerName: t('datasets.detail.grid.rowIdHeader'),
        width: 130,
        pinned: 'left' as const,
        editable: false,
        cellStyle: { fontFamily: 'monospace', fontSize: '11px' },
      },
      ...buildColumnDefs(fields, errorMap),
    ];
  }, [mapping.data, errorMap, t]);

  const gridRows = useMemo<GridRow[]>(() => {
    return (rows.data?.rows ?? []).map((row) => ({
      id: row.id,
      ...(row.data as Record<string, unknown>),
    }));
  }, [rows.data]);

  function defaultRowFromMapping(m: PropertyMapping): Record<string, unknown> {
    return Object.fromEntries(m.schema.fields.map((f) => [f.name, '']));
  }

  function handleAddRow(): void {
    if (!mapping.data) return;
    append.mutate([defaultRowFromMapping(mapping.data.mapping)]);
  }

  function handleDeleteSelected(): void {
    const selected = gridRef.current?.api.getSelectedRows() ?? [];
    if (selected.length === 0) {
      toast.info(t('datasets.detail.selectFirst'));
      return;
    }
    removeRows.mutate(selected.map((r) => r.id as string));
  }

  if (dataset.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[420px] w-full rounded-xl" />
      </div>
    );
  }
  if (dataset.isError || !dataset.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('datasets.detail.notFoundTitle')}</AlertTitle>
        <AlertDescription>{t('datasets.detail.notFoundBody')}</AlertDescription>
      </Alert>
    );
  }

  const current = dataset.data.dataset;

  return (
    <div className="space-y-6">
      <Link
        to="/app/datasets"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        {t('datasets.detail.backLink')}
      </Link>

      <PageHeader
        eyebrow={t('app.layout.nav.datasets', { defaultValue: 'Dataset' })}
        title={current.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="rounded-full">
              {t('datasets.detail.rowsBadge', {
                count: current.rowCount,
                defaultValue: '{{count}} rows',
              })}
            </Badge>
            <Badge variant="outline" className="rounded-full">
              {Math.round(current.sizeBytes / 1024)} KB
            </Badge>
            <span className="text-xs text-muted-foreground">
              {t('datasets.detail.updatedAt', {
                when: dateTime(current.updatedAt),
                defaultValue: 'Updated {{when}}',
              })}
            </span>
          </span>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleAddRow} disabled={append.isPending || !mapping.data}>
              {append.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Plus className="size-4" aria-hidden />
              )}
              {t('datasets.detail.addRow')}
            </Button>
            <Button
              variant="outline"
              onClick={handleDeleteSelected}
              disabled={removeRows.isPending}
            >
              {removeRows.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="size-4" aria-hidden />
              )}
              {t('datasets.detail.deleteSelected')}
            </Button>
            <Button
              variant="outline"
              onClick={() => validate.mutate()}
              disabled={validate.isPending}
            >
              {validate.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <ShieldCheck className="size-4" aria-hidden />
              )}
              {t('datasets.detail.validate')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => archive.mutate()}
              disabled={archive.isPending}
            >
              {archive.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="size-4" aria-hidden />
              )}
              {t('datasets.detail.archive')}
            </Button>
          </div>
        }
      />

      {report ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              {report.errorCount === 0 ? (
                <>
                  <CheckCircle2 className="size-4 text-emerald-600" aria-hidden />
                  {t('datasets.detail.report.ok')}
                </>
              ) : (
                <>
                  <TriangleAlert className="size-4 text-amber-600" aria-hidden />
                  {t('datasets.detail.report.fail', {
                    errors: report.errorCount,
                    rows: report.rowCount,
                  })}
                </>
              )}
            </CardTitle>
            <CardDescription>
              {report.errorCount === 0
                ? t('datasets.detail.report.okBody')
                : t('datasets.detail.report.failBody')}
            </CardDescription>
          </CardHeader>
          {report.errors.length > 0 ? (
            <CardContent>
              <ul className="max-h-60 space-y-1 overflow-auto text-xs text-muted-foreground">
                {report.errors.slice(0, 20).map((e, idx) => (
                  <li key={`${e.rowId}-${e.field}-${idx}`}>
                    <span className="font-mono text-foreground">{e.rowId}</span>·{' '}
                    <span className="font-medium">{e.field}</span>: {e.message}
                  </li>
                ))}
                {report.errors.length > 20 ? (
                  <li>
                    {t('datasets.detail.report.moreErrors', {
                      count: report.errors.length - 20,
                    })}
                  </li>
                ) : null}
              </ul>
            </CardContent>
          ) : null}
        </Card>
      ) : null}

      <div
        className={`${gridThemeClass} overflow-hidden rounded-xl border border-border/70 shadow-soft`}
        style={{ height: 560 }}
      >
        <AgGridReact<GridRow>
          ref={gridRef}
          rowData={gridRows}
          columnDefs={columnDefs}
          rowSelection={{ mode: 'multiRow' }}
          getRowId={(params) => params.data.id}
          defaultColDef={{ filter: true, sortable: true, resizable: true }}
          stopEditingWhenCellsLoseFocus
          onCellValueChanged={(event) => {
            const field = event.colDef.field;
            if (!field || field === 'id') return;
            const rowId = event.data?.id;
            if (!rowId) return;
            updateCell.mutate({ rowId, data: { [field]: event.newValue } });
          }}
          onGridReady={(event: GridReadyEvent<GridRow>) => {
            event.api.sizeColumnsToFit();
          }}
        />
      </div>

      {(() => {
        const total = rows.data?.total ?? 0;
        if (total <= PAGE_SIZE) return null;
        const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
        const safePage = Math.min(page, totalPages);
        const from = (safePage - 1) * PAGE_SIZE + 1;
        const to = Math.min(safePage * PAGE_SIZE, total);
        const items = buildPageList(safePage, totalPages);
        return (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {t('datasets.detail.pagination.summary', { from, to, total })}
            </span>
            <nav
              className="flex items-center gap-1"
              aria-label={t('datasets.detail.pagination.label', { defaultValue: 'Pagination' })}
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(safePage - 1)}
                disabled={safePage <= 1}
                aria-label={t('datasets.detail.pagination.previous')}
              >
                <ChevronLeft className="size-4" aria-hidden />
              </Button>
              {items.map((item) =>
                item === 'ellipsis-start' || item === 'ellipsis-end' ? (
                  <span key={item} className="px-2 text-xs text-muted-foreground" aria-hidden>
                    …
                  </span>
                ) : (
                  <Button
                    key={item}
                    variant={item === safePage ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPage(item)}
                    aria-current={item === safePage ? 'page' : undefined}
                    className="min-w-9"
                  >
                    {item}
                  </Button>
                ),
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(safePage + 1)}
                disabled={safePage >= totalPages}
                aria-label={t('datasets.detail.pagination.next')}
              >
                <ChevronRight className="size-4" aria-hidden />
              </Button>
            </nav>
          </div>
        );
      })()}
    </div>
  );
}
